import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { toast } from "react-toastify";

import { API_bulk_change_overrides } from "../../../../constants";
import { postRightworkAPI } from "../../requests/utilities/requests";
import {
  SPECIAL_ACCESS_ICONS,
  SPECIAL_ACCESS_STYLES,
} from "../../requests/manager/employees/EmployeesTableRow";
import ScopePicker from "./ScopePicker";
import { STORE_OVERRIDE_OPTIONS } from "./constants";
import { summarizeFailureReasons, logBulkFailures } from "./bulkResults";


function OverridePill({ value, label, mode, onClick }) {
  // mode: "off" | "add" | "remove"
  const Icon = SPECIAL_ACCESS_ICONS[value];
  const styles = SPECIAL_ACCESS_STYLES[value];
  const visualClass =
    mode === "add"
      ? "border-violet-600 bg-violet-50 text-violet-700"
      : mode === "remove"
      ? "border-rose-400 bg-rose-50 text-rose-700"
      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors ${visualClass}`}
    >
      {Icon && styles && (
        <span
          className={`inline-flex h-4 w-4 items-center justify-center rounded border ${styles.border} ${styles.bg}`}
        >
          <Icon className={styles.text} style={{ fontSize: 10 }} />
        </span>
      )}
      {label}
    </button>
  );
}


export default function BulkSpecialAccessModal({
  open,
  onClose,
  selectedUserIds,
  companyId,
  companyStores,
  onSuccess,
  initialScope,
  initialStoreIds,
  initialAddOverrides,
  initialRemoveOverrides,
  mirrorMode,
}) {
  const [scope, setScope] = React.useState("all");
  const [storeIds, setStoreIds] = React.useState([]);
  const [addOverrides, setAddOverrides] = React.useState([]);
  const [removeOverrides, setRemoveOverrides] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // initial* props let the drawer's per-row menu open us pre-filled.
      // Selection-based callers (toolbar) omit them.
      setScope(initialScope || "all");
      setStoreIds(initialStoreIds || []);
      setAddOverrides(initialAddOverrides || []);
      setRemoveOverrides(initialRemoveOverrides || []);
    }
  }, [open, initialScope, initialStoreIds, initialAddOverrides, initialRemoveOverrides]);

  const userCount = selectedUserIds?.size || 0;
  const hasSomethingToDo = addOverrides.length > 0 || removeOverrides.length > 0;
  const canSubmit =
    userCount > 0 &&
    !submitting &&
    hasSomethingToDo &&
    (scope === "all" || storeIds.length > 0);

  const cycleAdd = (value) => {
    setAddOverrides((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
    setRemoveOverrides((prev) => prev.filter((v) => v !== value));
  };

  const cycleRemove = (value) => {
    setRemoveOverrides((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
    setAddOverrides((prev) => prev.filter((v) => v !== value));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        company_id: companyId,
        user_ids: Array.from(selectedUserIds).join(","),
        scope,
        add_overrides: addOverrides.join(","),
        remove_overrides: removeOverrides.join(","),
      };
      if (scope === "stores") body.store_ids = storeIds.join(",");

      const response = await postRightworkAPI(API_bulk_change_overrides, body);
      const payload = response?.data?.redux?.payload || {};
      const succeeded = payload.succeeded_count || 0;
      const failed = payload.failed_count || 0;
      if (failed === 0) {
        toast.success(
          `Updated ${succeeded} membership${succeeded === 1 ? "" : "s"}.`,
          { autoClose: 3000 },
        );
      } else {
        const reasons = summarizeFailureReasons(payload.results);
        logBulkFailures("bulk_change_overrides", payload.results);
        toast.warning(
          `Updated ${succeeded} of ${succeeded + failed}; ${failed} failed.${reasons ? ` Reasons: ${reasons}` : ""}`,
          { autoClose: 8000 },
        );
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Bulk override change failed.",
        { autoClose: 4000 },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 560,
          maxHeight: "90vh",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Special access</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {mirrorMode ? (
            <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5">
              <p className="text-sm text-violet-900">
                Replacing special access at the selected stores with this row's exact set.
              </p>
              <p className="mt-1 text-xs text-violet-700">
                Overrides not in this row will be cleared from those memberships.
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              Granting or revoking special access for <span className="font-semibold">{userCount}</span> selected
              {userCount === 1 ? " user" : " users"}.
              {hasSomethingToDo && (
                <span className="block mt-1 text-xs text-slate-500">
                  Existing overrides not selected here are left untouched on each membership.
                </span>
              )}
            </p>
          )}

          <ScopePicker
            scope={scope}
            setScope={setScope}
            storeIds={storeIds}
            setStoreIds={setStoreIds}
            companyStores={companyStores}
            radioGroupName="bulk-overrides-scope"
          />

          <div>
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              <AddRoundedIcon style={{ fontSize: 14 }} />
              Grant
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STORE_OVERRIDE_OPTIONS.map((opt) => (
                <OverridePill
                  key={opt.value}
                  value={opt.value}
                  label={opt.label}
                  mode={addOverrides.includes(opt.value) ? "add" : "off"}
                  onClick={() => cycleAdd(opt.value)}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              <RemoveRoundedIcon style={{ fontSize: 14 }} />
              Revoke
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STORE_OVERRIDE_OPTIONS.map((opt) => (
                <OverridePill
                  key={opt.value}
                  value={opt.value}
                  label={opt.label}
                  mode={removeOverrides.includes(opt.value) ? "remove" : "off"}
                  onClick={() => cycleRemove(opt.value)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <LoadingButton
            variant="contained"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#6d28d9",
              "&:hover": { backgroundColor: "#5b21b6" },
              fontSize: 14,
              px: 2.5,
              py: 1,
            }}
          >
            Apply changes
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}
